import React from "react";
import {
  Box,
  Typography,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PerSegmentWeightRow from "./PerSegmentWeightRow";
import { usePerSegmentWeights } from "@/hooks/use-per-segment-weights";
import HPButton from "../common/Button";

interface PerSegmentWeightConfigProps {
  datasetId: string;
  columnLabels: string[]; // Array of column labels from crosstab
  customColumnLabels?: Record<string, string>; // Custom renamed labels for columns
  hiddenColumns?: string[]; // Array of hidden column labels
  columnVariableIds?: Record<string, string>; // Map of column label -> variable ID
  onWeightsChange: (
    weights: Record<
      string,
      {
        surveyId: string;
        configurationId: string;
        datasetElementQuestionId: string;
        datasetElementVariableId: string;
        weightVariableLabel: string;
        crossColumnVariableId?: string;
      }
    >,
  ) => void;
  disabled?: boolean;
}

const PerSegmentWeightConfig: React.FC<PerSegmentWeightConfigProps> = ({
  datasetId,
  columnLabels,
  customColumnLabels = {},
  hiddenColumns = [],
  columnVariableIds = {},
  onWeightsChange,
  disabled = false,
}) => {
  const {
    datasetId: resolvedDatasetId,
    isMultiSurvey,
    weightConfigurations,
    isLoadingWeights,
    updateColumnWeight,
    clearColumnWeight,
    clearAllWeights,
    isAllColumnsConfigured,
    getColumnWeight,
  } = usePerSegmentWeights({
    datasetId,
    columnLabels,
    columnVariableIds,
    onWeightsChange,
  });

  if (isMultiSurvey) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Per-segment weights for datasets with multiple surveys is not yet
        supported.
      </Alert>
    );
  }

  if (isLoadingWeights) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading weight configurations...
        </Typography>
      </Box>
    );
  }

  if (!weightConfigurations || weightConfigurations.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No weight configurations available for this dataset. Please configure
        weights in Dataset Configuration first.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Accordion
        defaultExpanded
        sx={{ boxShadow: "none", border: "1px solid #eee" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Per-segment weighting
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 1,
              }}
            >
              <HPButton
                size="small"
                variant="outlined"
                onClick={clearAllWeights}
                disabled={disabled || columnLabels.length === 0}
              >
                Set all to Default Weight
              </HPButton>
            </Box>

            {!isAllColumnsConfigured && columnLabels.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: "0.875rem" }}>
                Please configure weight for all columns to preview data.
              </Alert>
            )}

            {columnLabels.length === 0 && (
              <Alert severity="info" sx={{ mb: 2, fontSize: "0.875rem" }}>
                Waiting for crosstab data to load column information...
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {columnLabels.map((columnLabel, index) => (
                <React.Fragment key={columnLabel}>
                  <PerSegmentWeightRow
                    columnLabel={columnLabel}
                    displayLabel={
                      customColumnLabels[columnLabel] || columnLabel
                    }
                    datasetId={resolvedDatasetId}
                    weightConfigurations={weightConfigurations}
                    selectedWeight={getColumnWeight(columnLabel)}
                    onWeightChange={(
                      surveyId,
                      configurationId,
                      datasetElementQuestionId,
                      datasetElementVariableId,
                      variableLabel,
                    ) =>
                      updateColumnWeight(
                        columnLabel,
                        surveyId,
                        configurationId,
                        datasetElementQuestionId,
                        datasetElementVariableId,
                        variableLabel,
                      )
                    }
                    onClearWeight={clearColumnWeight}
                    isHiddenColumn={hiddenColumns.includes(columnLabel)}
                    disabled={disabled}
                  />
                  {index < columnLabels.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PerSegmentWeightConfig;
